import React, { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import {
  ChevronDown,
  Linkedin,
  Mail,
  Code,
  Database,
  Server,
} from "lucide-react";
import { Element, Link } from "react-scroll";
import { useTranslation } from "react-i18next";
// @ts-ignore
import cv from "../assets/cv.pdf";

const Hero: React.FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 300], [0, 100]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const floatingVariants = {
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  const techStack = [
    { icon: Code, label: "ReactJS", color: "text-blue-400" },
    { icon: Code, label: "NodeJS", color: "text-green-400" },
    { icon: Server, label: "AWS", color: "text-red-400" },
    { icon: Database, label: "MySQL", color: "text-orange-400" },
    { icon: Database, label: "MongoDB", color: "text-green-500" },
    { icon: Database, label: "ClickHouse", color: "text-yellow-400" },
    { icon: Database, label: "Snowflake", color: "text-cyan-400" },
  ];

  return (
    <Element name="hero">
      <section
        ref={ref}
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-12"
      >
        <motion.div style={{ y }} className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-slate-900/20" />

          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow" />
          <div
            className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse-slow"
            style={{ animationDelay: "1s" }}
          />
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl animate-pulse-slow"
            style={{ animationDelay: "2s" }}
          />
        </motion.div>

        <div className="relative z-10 section-padding container-width">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-center"
          >
            <motion.div variants={itemVariants} className="mb-8">
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight">
                <span className="gradient-text">{t("hero.name")}</span>
              </h1>
              <p className="text-xl md:text-2xl lg:text-3xl text-gray-300 font-light">
                {t("hero.title")}
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-12">
              <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                {t("hero.description")}
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-16">
              <div className="flex flex-wrap justify-center gap-6 mb-8">
                {/* <motion.a
                  href="https://github.com/jamesbell37"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-card p-4 hover:bg-white/20 transition-all duration-300 group"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Github className="w-8 h-8 text-white group-hover:text-blue-400 transition-colors" />
                </motion.a> */}

                <motion.a
                  href="https://linkedin.com/in/jamessebastianbell"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-card p-4 hover:bg-white/20 transition-all duration-300 group"
                  whileHover={{ scale: 1.1, rotate: -5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Linkedin className="w-8 h-8 text-white group-hover:text-blue-400 transition-colors" />
                </motion.a>

                <motion.a
                  href="mailto:belljames79@gmail.com"
                  className="glass-card p-4 hover:bg-white/20 transition-all duration-300 group"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Mail className="w-8 h-8 text-white group-hover:text-blue-400 transition-colors" />
                </motion.a>
              </div>

              <motion.div className="flex flex-wrap justify-center gap-4">
                <motion.a
                  href={cv}
                  download="James-Bell-CV.pdf"
                  className="btn-primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t("hero.downloadCv")}
                </motion.a>

                <motion.button
                  className="btn-secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link to={"work"} smooth={true} duration={100} offset={-80}>
                    {t("hero.exploreWork")}
                  </Link>
                </motion.button>
              </motion.div>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-16">
              <h3 className="text-xl md:text-2xl font-semibold text-white mb-8">
                {t("hero.techStack")}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6">
                {techStack.map((tech, index) => (
                  <motion.div
                    key={tech.label}
                    className="glass-card p-6 text-center hover:bg-white/20 transition-all duration-300 group"
                    variants={itemVariants}
                    whileHover={{
                      scale: 1.1,
                      rotate: [0, -5, 5, 0],
                      transition: { duration: 0.3 },
                    }}
                    custom={index}
                  >
                    <tech.icon
                      className={`w-8 h-8 mx-auto mb-3 ${tech.color} group-hover:scale-110 transition-transform`}
                    />
                    <p className="text-sm text-gray-300 font-medium">
                      {tech.label}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              variants={floatingVariants}
              animate="animate"
              className="flex mb-4 justify-center mx-auto transform -translate-x-1/2 cursor-pointer"
            >
              <Link to={"about"} smooth={true} duration={100} offset={-80}>
                <ChevronDown className="w-8 h-8 text-white/60" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Element>
  );
};

export default Hero;
